import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  discoverClient,
  type DiscoverApp,
  type DiscoverPage,
} from "../discoverClient";
import { presetDiscoverApps } from "../presetApps";
import { bootedSignedOut } from "./signedOutBoot";
import {
  filterDiscoveryCatalogue,
  normalizeDiscoveryCatalogue,
} from "./discoveryCatalogue";
import type { AccountAuthState } from "@/auth/accountTypes";

interface CatalogueSnapshot {
  key: string;
  status: "loading" | "ready" | "error";
  apps: DiscoverApp[];
  nextCursor: string | null;
  categories: DiscoverPage["categories"];
  developer: DiscoverPage["developer"];
  loadingMore: boolean;
  error: string | null;
}

const loadingSnapshot = (key: string): CatalogueSnapshot => ({
  key,
  status: "loading",
  apps: [],
  nextCursor: null,
  categories: [],
  developer: null,
  loadingMore: false,
  error: null,
});

type Options = {
  authStatus?: AccountAuthState["status"];
  query?: string;
  category?: string | null;
  sort?: "recommended" | "new" | "rating" | "popular";
  limit?: number;
  exclude?: string;
  developer?: string;
  enabled?: boolean;
};

/**
 * Viewer reads wait for settled auth, unless the load began with no session
 * cookie: then the viewer is signed out and the page needs no wait.
 * Filtering/paging happens at the server.
 */
export function useDiscoveryCatalogue(
  userId: string | null,
  dismissedPresetIds: readonly string[],
  options: Options = {},
) {
  const authStatus =
    options.authStatus ?? (userId ? "signed_in" : "signed_out");
  const enabled =
    options.enabled !== false &&
    (authStatus === "signed_out" ||
      (authStatus === "loading" && bootedSignedOut()) ||
      (authStatus === "signed_in" && !!userId));
  const scope = authStatus === "signed_in" ? userId : null;
  const query = options.query ?? "";
  const category = options.category ?? null;
  const sort = options.sort ?? "recommended";
  const limit = options.limit ?? 12;
  const exclude = options.exclude;
  const developer = options.developer;
  const presets = useMemo(
    () => presetDiscoverApps(dismissedPresetIds),
    [dismissedPresetIds],
  );
  const presetEntries = useMemo(
    () =>
      developer ? [] : filterDiscoveryCatalogue(normalizeDiscoveryCatalogue([], presets), {
        query,
        themeId: category,
        sort,
      }).filter((entry) => !category && entry.selection.id !== exclude),
    [presets, query, category, sort, exclude, developer],
  );
  const pageLimit = Math.max(1, limit - presetEntries.length);
  const key = JSON.stringify([
    scope,
    query,
    category,
    sort,
    pageLimit,
    exclude,
    developer,
  ]);
  const edits = useRef(new Map<string, number>());
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const [snapshot, setSnapshot] = useState(() => loadingSnapshot(key));
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const moreRequest = useRef<AbortController | null>(null);
  const activeKey = useRef(key);
  activeKey.current = enabled ? key : "";

  const preserveEdits = useCallback(
    (
      apps: DiscoverApp[],
      current: CatalogueSnapshot,
      requested: Map<string, number>,
    ) =>
      apps.map((app) => {
        const mutationKey = `${scope}:${app.id}`;
        const edited =
          current.key === key &&
          edits.current.get(mutationKey) !== requested.get(mutationKey)
            ? current.apps.find((old) => old.id === app.id)
            : null;
        return edited
          ? {
              ...app,
              stats: edited.stats,
              viewer: edited.viewer,
              added: edited.added,
              instance_id: edited.instance_id,
              app_url: edited.app_url,
            }
          : app;
      }),
    [key, scope],
  );

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const requested = new Map(edits.current);
    setSnapshot((current) =>
      current.key === key && current.apps.length
        ? { ...current, status: "loading", loadingMore: false, error: null }
        : loadingSnapshot(key),
    );
    void Promise.resolve()
      .then(() =>
        controller.signal.aborted
          ? null
          : discoverClient.page(
              {
                q: query,
                category: category ?? undefined,
                sort,
                limit: pageLimit,
                exclude,
                developer,
              },
              controller.signal,
            ),
      )
      .then((page) => {
        if (page && !controller.signal.aborted && activeKey.current === key)
          setSnapshot((current) => ({
            key,
            status: "ready",
            apps: preserveEdits(page.apps, current, requested),
            nextCursor: page.next_cursor ?? null,
            categories: page.categories ?? [],
            developer: page.developer ?? null,
            loadingMore: false,
            error: null,
          }));
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted && activeKey.current === key)
          setSnapshot((current) => ({
            ...(current.key === key ? current : loadingSnapshot(key)),
            status: "error",
            loadingMore: false,
            error:
              cause instanceof Error
                ? cause.message
                : "The catalogue could not load.",
          }));
      });
    return () => {
      controller.abort();
      moreRequest.current?.abort();
      moreRequest.current = null;
    };
  }, [
    enabled,
    key,
    query,
    category,
    sort,
    pageLimit,
    exclude,
    developer,
    revision,
    preserveEdits,
  ]);

  const loadMore = useCallback(() => {
    const current = snapshotRef.current;
    if (
      !enabled ||
      current.key !== key ||
      current.status !== "ready" ||
      !current.nextCursor ||
      moreRequest.current
    )
      return;
    const controller = new AbortController();
    moreRequest.current = controller;
    const requested = new Map(edits.current);
    setSnapshot((value) => ({ ...value, loadingMore: true, error: null }));
    void discoverClient
      .page(
        {
          q: query,
          category: category ?? undefined,
          sort,
          limit: 12,
          exclude,
          developer,
          cursor: current.nextCursor,
        },
        controller.signal,
      )
      .then((page) => {
        if (controller.signal.aborted || activeKey.current !== key) return;
        setSnapshot((value) => {
          const incoming = preserveEdits(page.apps, value, requested);
          const existing = new Set(value.apps.map((app) => app.id));
          return {
            ...value,
            apps: [
              ...value.apps,
              ...incoming.filter((app) => !existing.has(app.id)),
            ],
            nextCursor: page.next_cursor ?? null,
            loadingMore: false,
          };
        });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || activeKey.current !== key) return;
        if (
          cause &&
          typeof cause === "object" &&
          "code" in cause &&
          cause.code === "discover_cursor_stale"
        ) {
          refresh();
        } else {
          setSnapshot((value) => ({
            ...value,
            loadingMore: false,
            error:
              cause instanceof Error
                ? cause.message
                : "More apps could not load.",
          }));
        }
      })
      .finally(() => {
        if (moreRequest.current === controller) moreRequest.current = null;
      });
  }, [enabled, key, query, category, sort, exclude, developer, preserveEdits, refresh]);

  const current =
    enabled && snapshot.key === key ? snapshot : loadingSnapshot(key);
  const entries = useMemo(
    () => [...normalizeDiscoveryCatalogue(current.apps, []), ...presetEntries],
    [current.apps, presetEntries],
  );
  const updateApp = useCallback(
    (id: string, update: (app: DiscoverApp) => DiscoverApp) => {
      const mutationKey = `${scope}:${id}`;
      edits.current.set(mutationKey, (edits.current.get(mutationKey) ?? 0) + 1);
      setSnapshot((value) =>
        value.key === key
          ? {
              ...value,
              apps: value.apps.map((app) =>
                app.id === id ? update(app) : app,
              ),
            }
          : value,
      );
    },
    [key, scope],
  );
  return {
    status: authStatus === "error" ? ("error" as const) : current.status,
    entries,
    apps: current.apps,
    categories: current.categories,
    developer: current.developer,
    error:
      authStatus === "error"
        ? "Your account could not be verified."
        : current.error,
    hasMore: !!current.nextCursor,
    loadingMore: current.loadingMore,
    refresh,
    updateApp,
    loadMore,
  };
}
